import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  LogOut,
  User,
  Search,
  CalendarDays,
  Home,
} from "lucide-react";
import NotificationBell from "../../components/NotificationBell";
import AppLogo from "../../components/AppLogo";

export default function PatientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.full_name || "?")
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="min-h-screen bg-mb-bg text-white">
      <header className="sticky top-0 z-10 flex h-16 items-center gap-6 border-b border-mb-border bg-mb-bg/95 px-5 backdrop-blur lg:px-10">
        <div className="shrink-0" data-testid="patient-brand">
          <AppLogo
            to="/patient"
            subtitle="Patient Portal"
            size={40}
          />
        </div>

        <nav className="flex flex-1 items-center gap-1 sm:gap-3">
          <PatientNavLink
            to="/patient"
            end
            testid="patient-nav-home"
            icon={<Home className="h-4 w-4" />}
          >
            Home
          </PatientNavLink>

          <PatientNavLink
            to="/patient/hospitals"
            testid="patient-nav-find"
            icon={<Search className="h-4 w-4" />}
          >
            Find Doctors
          </PatientNavLink>

          <PatientNavLink
            to="/patient/appointments"
            testid="patient-nav-appointments"
            icon={<CalendarDays className="h-4 w-4" />}
          >
            My Appointments
          </PatientNavLink>
        </nav>

        <div className="flex items-center gap-2">
          <NotificationBell testid="patient-notification-bell" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="patient-user-menu"
                variant="ghost"
                className="h-10 gap-2 rounded-lg px-3 hover:bg-mb-surface2"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-500/15 text-xs font-medium text-teal-300">
                  {initials}
                </div>

                <span className="hidden text-sm md:inline">
                  {user?.full_name?.split(" ")[0]}
                </span>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              className="border-mb-border bg-mb-surface2 text-white"
            >
              <DropdownMenuLabel className="text-xs uppercase tracking-[0.2em] text-mb-fg2">
                {user?.email}
              </DropdownMenuLabel>

              <DropdownMenuSeparator className="bg-mb-border" />

              <DropdownMenuItem asChild>
                <Link
                  to="/patient/profile"
                  data-testid="patient-menu-profile"
                  className="cursor-pointer"
                >
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={onLogout}
                data-testid="patient-menu-logout"
                className="cursor-pointer text-red-400 focus:text-red-400"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-10">
        <Outlet />
      </main>
    </div>
  );
}

function PatientNavLink({ to, end, children, icon, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
          isActive
            ? "bg-teal-500/10 text-teal-300"
            : "text-mb-fg2 hover:bg-mb-surface2 hover:text-white"
        }`
      }
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
    </NavLink>
  );
}